import React from "react";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/ui/button";
import type { ResetChannel } from "@/lib/api";

/**
 * Shared "Reset password" channel logic, reused by the admin (User Details) and
 * self-service (UserMenu) UIs. Returns a trigger handler + a stable dialog node.
 *
 * Availability + masked destinations come from the BACKEND
 * (/reset-availability), which applies the authoritative eligibility rule
 * (email present; phone present + verified + valid E.164 + allowed country). The
 * client never re-derives eligibility or handles full destinations.
 *
 * - both available → masked channel-selection dialog before sending
 * - one available  → initiate it directly (no unnecessary dialog)
 * - neither        → `disabled` true with an explanatory `title`
 *
 * The dialog node is returned separately so callers can render it OUTSIDE a
 * dropdown menu (which unmounts on close).
 */
export function useResetPassword({
  emailAvailable,
  smsAvailable,
  maskedEmail,
  maskedPhone,
  loading = false,
  error = false,
  initiate,
}: {
  emailAvailable: boolean;
  smsAvailable: boolean;
  maskedEmail?: string | null;
  maskedPhone?: string | null;
  /** Authoritative availability is still loading — block initiation. */
  loading?: boolean;
  /** Availability request failed — block initiation. */
  error?: boolean;
  initiate: (channel: ResetChannel) => Promise<void>;
}) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const none = !emailAvailable && !smsAvailable;
  // Initiation is only allowed once authoritative availability has loaded OK and
  // at least one method exists.
  const blocked = loading || error || none;

  async function run(channel: ResetChannel) {
    setSending(true);
    try {
      await initiate(channel);
      setDialogOpen(false);
    } finally {
      setSending(false);
    }
  }

  function onTrigger() {
    if (blocked || sending) return;
    if (emailAvailable && smsAvailable) setDialogOpen(true);
    else void run(emailAvailable ? "email" : "sms");
  }

  // Distinct states — loading and error must NOT look like "no methods".
  let label: string;
  let title: string;
  if (loading) {
    label = "Checking recovery options…";
    title = label;
  } else if (error) {
    label = "Could not load recovery options";
    title = label;
  } else if (none) {
    label = "Reset password";
    title = "No recovery email or phone number is configured for this account";
  } else {
    label = sending ? "Sending…" : "Reset password";
    title = "Send a password reset";
  }

  const dialog = (
    <Modal
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      title="How would you like to send the password reset?"
    >
      <div className="space-y-3">
        <Button className="w-full justify-start" variant="outline"
                disabled={sending} onClick={() => void run("email")}>
          Email: {maskedEmail}
        </Button>
        <Button className="w-full justify-start" variant="outline"
                disabled={sending} onClick={() => void run("sms")}>
          Text message: {maskedPhone}
        </Button>
        {/* Consent reminder — the permanent opt-in still comes from Account
            Settings; choosing this channel does not create consent. */}
        <p className="px-1 text-xs opacity-60">
          VoltiE will send a one-time password-reset code to this verified number.
          Msg &amp; data rates may apply. Reply STOP to opt out or HELP for help.
        </p>
        <Button className="w-full" variant="ghost"
                disabled={sending} onClick={() => setDialogOpen(false)}>
          Cancel
        </Button>
      </div>
    </Modal>
  );

  return {
    onTrigger,
    dialog,
    disabled: blocked || sending,
    title,
    label,
  };
}
